// @flow
import React, {useEffect} from 'react';
import classnames from 'classnames';
import {compose} from 'redux';
import {connect, Provider, useStore} from 'react-redux';
import {injectIntl, FormattedMessage} from 'react-intl';
import {Map} from '@homeaway/react-map-components';
import {
    PLUS__16,
    MINUS__16,
    FILTER__16,
    CHEVRON_LEFT__16
} from '@homeaway/svg-defs';
import {SVGIcon} from '@homeaway/react-svg';
import {EdapProvider} from '@homeaway/react-event-tracking';
import {API_KEY} from './helpers';
import LoadingPlaceholder from '../LoadingPlaceholder/LoadingPlaceholder';
import mapAction from '../../actions/map';
import {trigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import routerActions from '../../actions/router';
import {media} from '../../utils/breakpoints';
import FiltersContainer from '../Filters/FiltersContainer';
import {filterCount} from '../../utils/filterCount';
// $FlowFixMe
import TripBoardButton from '../TripBoardButton/TripBoardButton';
import type {IntlShape} from 'react-intl';
import type {Breakpoint} from '../../utils/breakpoints';
import type {State as ReduxState} from '../../reducers/index';
import {getLanguageFromLocale, getRegion} from '../../utils/localization';
import {MAP_VIEW} from '../../constants/views';
import './MapContainer.less';
import type {ListingShape as HitListingShape} from '../Hit/Hit';

export type MapViewport = {
    neLat: number,
    neLong: number,
    swLat: number,
    swLong: number
};

export type MapCentralPin = {
    lat?: number,
    lng?: number
};

type MapOptions = {|
    zoomControl?: boolean
|};

export type GoogleMapState = {
    bounds: MapViewport,
    zoom: number
};

export type MapHitListingShape = {
    geoCode?: {
        latitude: string,
        longitude: string
    },
    tripBoardButton: () => any,
    pixelOffset: {
        width: number,
        height: number
    },
    infoBoxClearance: {
        width: number,
        height: number
    }
} & HitListingShape;

type StateProps = {
    bounds?: MapViewport | null,
    mapCentralPin?: MapCentralPin | null,
    zoom?: number | null,
    highlightedPin?: string | null,
    mapMarkersClicked: Object,
    shouldSearchOnMove: boolean,
    isSearchButtonVisible?: boolean | null,
    shouldDisableSearchButton?: boolean | null,
    breakpoint: Breakpoint,
    mapHasLoaded?: boolean | null,
    fullLocale: string | null,
    shouldMapBeExpanded: boolean,
    isTouchDevice: boolean,
    isMapView: boolean
};

type Props = {
    siteName: string,
    allowUpdate: boolean,
    intl: IntlShape,
    loading: boolean,
    shouldShowFiltersButton: boolean,
    onBackClick?: () => void,
    listings: MapHitListingShape[],
    options: MapOptions,
    applicationMapStyles: Object,
    favoredListings: string[]
};

type DispatchProps = {
    addPinClicked: typeof mapAction.addPinClicked,
    updateMap: typeof mapAction.updateMap,
    setMapMoveState: typeof mapAction.setMapMoveState,
    showMapSearchButton: typeof mapAction.mapShowSearchButton,
    disableSearchOnMove: typeof mapAction.disableSearchOnMove,
    enableSearchOnMove: typeof mapAction.enableSearchOnMove,
    mapIdle: typeof mapAction.mapIdle,
    resetSearchButton: typeof mapAction.mapResetSearchButton,
    syncBounds: typeof mapAction.syncBounds,
    mapSearch: typeof routerActions.mapSearch
};

type OwnProps = StateProps & Props & DispatchProps;

export const MapContainer = (props: OwnProps) => {
    const {
        bounds,
        zoom,
        intl,
        shouldSearchOnMove,
        isSearchButtonVisible,
        shouldDisableSearchButton,
        loading,
        breakpoint,
        allowUpdate,
        onBackClick,
        shouldShowFiltersButton,
        mapIdle,
        updateMap,
        showMapSearchButton,
        setMapMoveState,
        mapSearch,
        resetSearchButton,
        syncBounds,
        enableSearchOnMove,
        disableSearchOnMove,
        mapHasLoaded,
        fullLocale,
        shouldMapBeExpanded,
        options: {zoomControl},
        isTouchDevice,
        isMapView,
        applicationMapStyles
    } = props;

    const MAP_VIEW_CLASSNAME = 'map-view';
    const isDesktop = media(breakpoint, {minWidth: 'MD_MIN'});
    const isMobile = !media(breakpoint, {minWidth: 'SM_MIN'});
    const store = useStore();

    useEffect(() => {
        const body = window && window.document.body;
        if (
            (isTouchDevice || isMobile) &&
            isMapView &&
            body &&
            body.classList
        ) {
            body.classList.add(MAP_VIEW_CLASSNAME);
        } else {
            body.classList.remove(MAP_VIEW_CLASSNAME);
        }
    }, [isMapView, isTouchDevice, isMobile]);

    const labels = {
        autosearch: intl.formatMessage({
            id: 'autoSearchLabel',
            defaultMessage: 'Search when I move map'
        })
    };

    const options = {
        center: bounds && {lat: bounds.neLat, lng: bounds.swLong},
        disableDefaultUI: true,
        draggable: true,
        fullscreenControl: false,
        gestureHandling: 'greedy',
        mapTypeControl: false,
        panControl: false,
        scaleControl: true,
        scrollwheel: false,
        streetViewControl: false
    };
    const mapData = {
        apiConfigOptions: {
            apiKey: API_KEY,
            appName: 'SERP',
            language: getLanguageFromLocale(fullLocale) || 'en',
            region: getRegion(fullLocale) || 'us',
            siteName: 'vrbo'
        },
        labels,
        exact: true,
        options,
        bounds,
        zoom,
        preventDefaultZoom: true,
        preventFitBounds: false
    };

    const mapClassNames = classnames('Map', {Map__loading: loading});

    const createZoomControl = () => {
        if (zoomControl) {
            const controlDiv = document.createElement('div');
            controlDiv.innerHTML = `<div class="Map__zoom-wrapper Map__control"><div class="Map__zoom-in">${PLUS__16}</div><div class="Map__zoom-divider"></div><div class="Map__zoom-out">${MINUS__16}</div></div>`;
            return controlDiv;
        }
        return null;
    };

    const handleBackClick = () => {
        trigger(GENERIC_EVENT, {
            eventcategory: 'search',
            eventaction: 'map to list click',
            eventlabel: 'button'
        });

        /* istanbul ignore else */
        if (props.onBackClick) {
            props.onBackClick();
        }
    };

    const triggerFilterClickedEdapEvent = () => {
        trigger(GENERIC_EVENT, {
            eventaction: 'filter menu clicked',
            eventcategory: 'search'
        });
    };

    const renderFiltersButton = ({
        appliedCount,
        filtersButtonClickHandler
    }: {
        appliedCount: number,
        filtersButtonClickHandler: Function
    }) => {
        return (
            <button
                className={classnames(
                    'Map__control',
                    'Map__control--top-right',
                    'Map__control--filters-button'
                )}
                data-wdio="Map__filtersButton"
                onClick={filtersButtonClickHandler}
            >
                <SVGIcon markup={FILTER__16} className="text-link" />
                <FormattedMessage
                    id="filtersButton"
                    description="Button to open Filters menu"
                    defaultMessage="Filters {filterNumber}"
                    values={{
                        filterNumber: filterCount(appliedCount)
                    }}
                />
            </button>
        );
    };

    const allFiltersButtonCustomTrigger = ({
        appliedCount,
        onClick
    }: {
        appliedCount: number,
        onClick: Function
    }) => {
        const handleFiltersButtonClick = () => {
            triggerFilterClickedEdapEvent();
            // open "more filters" modal
            onClick();
        };

        return renderFiltersButton({
            filtersButtonClickHandler: handleFiltersButtonClick,
            appliedCount
        });
    };

    const getHoveredMarker = () => {
        const {listings, highlightedPin} = props;
        const hoveredMarker =
            listings &&
            listings.filter((listing) => listing.listingId === highlightedPin);
        if (
            !hoveredMarker ||
            !hoveredMarker[0] ||
            !hoveredMarker[0].geoCode ||
            !hoveredMarker[0].geoCode.latitude ||
            !hoveredMarker[0].geoCode.longitude
        ) {
            return null;
        }
        return `${hoveredMarker[0].geoCode.latitude}${hoveredMarker[0].geoCode.longitude}`;
    };

    const getMarkers = () => {
        const {
            listings,
            highlightedPin,
            siteName,
            mapMarkersClicked,
            mapCentralPin,
            favoredListings
        } = props;
        const markers = [];
        let marker;
        if (listings) {
            listings.map((listing, index) => {
                if (
                    !listing ||
                    !listing.geoCode ||
                    !listing.geoCode.latitude ||
                    !listing.geoCode.longitude
                ) {
                    return null;
                }

                const isHighlighted = listing.listingId === highlightedPin;
                marker = {
                    id: `marker-${index}`,
                    position: {
                        lat: listing.geoCode.latitude,
                        lng: listing.geoCode.longitude
                    },
                    siteName,
                    listingId: listing.listingId,
                    isHighlighted,
                    isVisited: mapMarkersClicked[listing.listingId],
                    infoWindowOptions: listing,
                    isFavored: favoredListings.includes(listing.listingId)
                };

                marker.infoWindowOptions.tripBoardButton = () => (
                    <TripBoardButton listing={listing} isMapHit />
                );
                marker.infoWindowOptions.pixelOffset = {
                    width: -123, // negative value is given to align the infowindow towards left
                    height: -36 // negative value is given to align the infowindow towards top
                };
                marker.infoWindowOptions.infoBoxClearance = {
                    width: 10,
                    height: 10
                };
                marker.infoWindowOptions.priceSummary = listing.priceSummary;
                marker.infoWindowOptions.priceSummarySecondary =
                    listing.priceSummarySecondary;

                markers.push(marker);
                return markers;
            });
            if (mapCentralPin) {
                marker = {
                    id: `central-marker`,
                    position: mapCentralPin,
                    isHighlighted: false,
                    zIndex: 10000000
                };

                markers.push(marker);
            }
        }
        return markers;
    };

    const onMarkerClick = (uuid) => {
        const {listings, addPinClicked} = props;
        listings.map((listing) => {
            if (
                listing &&
                listing.geoCode &&
                listing.geoCode.latitude &&
                listing.geoCode.longitude &&
                `${listing.geoCode.latitude}${listing.geoCode.longitude}` ===
                    uuid
            ) {
                addPinClicked(listing.listingId);
            }
            return listing;
        });
    };

    const MapHitComponentProvider = (args) => (
        <Provider store={store}>{args.children}</Provider>
    );

    return (
        <div className={mapClassNames} data-wdio="Map">
            {onBackClick && (
                <button
                    data-wdio="Map__backButton"
                    className={classnames(
                        'Map__control',
                        'Map__control--top-left',
                        'Map__control--back-button'
                    )}
                    onClick={handleBackClick}
                >
                    <SVGIcon className="text-link" markup={CHEVRON_LEFT__16} />
                    <FormattedMessage
                        id="backToListButton"
                        description="Back to List button on map page"
                        defaultMessage="List"
                    />
                </button>
            )}
            {shouldShowFiltersButton && (
                <FiltersContainer
                    allFiltersButtonOnly
                    allFiltersButtonCustomTrigger={
                        allFiltersButtonCustomTrigger
                    }
                    searchResultsLoading={loading}
                />
            )}
            {bounds ? (
                <div className="Map__container" style={applicationMapStyles}>
                    <div className="Map__map">
                        <EdapProvider>
                            <Map
                                {...mapData}
                                markers={getMarkers()}
                                showAutoSearchCheckBox={!isMobile}
                                shouldSearchOnMove={shouldSearchOnMove}
                                showSearchAreaButton={
                                    isSearchButtonVisible && !shouldSearchOnMove
                                }
                                disableSearchAreaButton={
                                    shouldDisableSearchButton
                                }
                                isLoading={loading && !isDesktop}
                                allowMapUpdate={allowUpdate}
                                onIdle={mapIdle}
                                updateMap={updateMap}
                                showMapSearchButton={showMapSearchButton}
                                setMapMoveState={setMapMoveState}
                                mapSearch={mapSearch}
                                resetSearchButton={resetSearchButton}
                                syncBounds={syncBounds}
                                enableSearchOnMove={enableSearchOnMove}
                                disableSearchOnMove={disableSearchOnMove}
                                customZoomControl={createZoomControl}
                                hoveredMarker={getHoveredMarker()}
                                mapHasLoaded={mapHasLoaded}
                                onMarkerClick={onMarkerClick}
                                showLoadingElement={false}
                                shouldMapBeExpanded={shouldMapBeExpanded}
                                MapHitComponentProvider={
                                    MapHitComponentProvider
                                }
                            />
                        </EdapProvider>
                    </div>
                </div>
            ) : (
                <LoadingPlaceholder extraClasses="Map--loading" />
            )}
        </div>
    );
};

const mapStateToProps = ({
    searchParams: {
        params: {view}
    },
    map: {
        bounds,
        zoom,
        mapCentralPin,
        listingId,
        mapMarkersClicked,
        shouldSearchOnMove,
        isSearchButtonVisible,
        shouldDisableSearchButton,
        mapHasLoaded
    },
    tripboardButton: {tripBoardButtonOperations},
    viewport: {breakpoint},
    system: {isMobile, isTablet, fullLocale},
    expandedGroups: {hasModifiedMapViewport}
}: ReduxState): StateProps => ({
    isTouchDevice: isMobile || isTablet,
    bounds,
    mapCentralPin,
    zoom,
    highlightedPin: listingId,
    mapMarkersClicked,
    shouldSearchOnMove,
    isSearchButtonVisible,
    shouldDisableSearchButton,
    breakpoint,
    mapHasLoaded,
    fullLocale,
    shouldMapBeExpanded: hasModifiedMapViewport,
    isMapView: view === MAP_VIEW,
    favoredListings: tripBoardButtonOperations.allListingRefs
});
const mapDispatchToProps = {
    addPinClicked: mapAction.addPinClicked,
    updateMap: mapAction.updateMap,
    setMapMoveState: mapAction.setMapMoveState,
    showMapSearchButton: mapAction.mapShowSearchButton,
    enableSearchOnMove: mapAction.enableSearchOnMove,
    disableSearchOnMove: mapAction.disableSearchOnMove,
    mapIdle: mapAction.mapIdle,
    mapSearch: routerActions.mapSearch,
    resetSearchButton: mapAction.mapResetSearchButton,
    syncBounds: mapAction.syncBounds
};

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(MapContainer);
